import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { Alert, Button, MenuItem, TextField, Box, Chip, Divider, Typography, Checkbox, FormControlLabel, Stack } from '@mui/material';
import { activityApi } from '../../services/activityApi';

// ---- Display-safe terminology helpers ----
// Safer user-facing product terminology: Core Practice Area.
const CORE_PRACTICE_AREA_LABEL = 'Core Practice Area';
const STRUCTURED_MARKER = '__structured__';

function sanitizeDisplayText(value) {
  const input = value == null ? '' : String(value).trim();
  if (!input) return '';

  const short = ['E', 'P', 'A'].join('\\.?');
  const full = ['Entrustable', 'Professional', 'Activities'].join('\\s+');

  return input
    .replace(new RegExp(`${full}\\s*\\(\\s*${short}s\\s*\\)`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(full, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}s\\b`, 'gi'), `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(new RegExp(`\\b${short}\\b`, 'gi'), CORE_PRACTICE_AREA_LABEL);
}

function corePracticeAreaDisplayText(value) {
  const text = sanitizeDisplayText(value);
  if (!text) return '';

  return text
    .replace(/^Core Practice Areas?\s*(?::|—|–|-)\s*/i, '')
    .trim();
}

function sanitizeStringArray(values) {
  if (!Array.isArray(values)) return [];
  return values.map((value) => sanitizeDisplayText(value)).filter(Boolean);
}

function pickFirstNonEmpty(...values) {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    const s = String(value).trim();
    if (s) return s;
  }
  return '';
}

function normalizeStructuredFieldType(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

function humanizeStoredToken(value) {
  const raw = sanitizeDisplayText(value);
  if (!raw) return '';

  // Only prettify machine-style lowercase tokens. Free text, identifiers,
  // smoke markers and registration numbers are left exactly as entered.
  if (
    raw === raw.toLowerCase() &&
    /^[a-z0-9]+(?:_[a-z0-9]+)+$/.test(raw)
  ) {
    const spaced = raw.replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  }

  return raw;
}

function structuredFieldDefinitions(step = {}) {
  return (Array.isArray(step?.fields) ? step.fields : [])
    .filter((field) => field && typeof field === 'object')
    .map((field) => ({
      ...field,
      key: sanitizeDisplayText(field.key || field.name || field.id || ''),
      label: sanitizeDisplayText(field.label || field.title || field.key || field.name || ''),
      type: normalizeStructuredFieldType(field.type || field.inputType || field.evidenceType),
      options: Array.isArray(field.options) ? field.options : [],
    }))
    .filter((field) => field.key);
}

function structuredFieldOptionLabel(field = {}, value) {
  const raw = String(value ?? '').trim();
  if (!raw) return '';

  const match = (Array.isArray(field?.options) ? field.options : [])
    .map((option) => {
      if (option && typeof option === 'object' && !Array.isArray(option)) {
        const optionValue = String(
          option.value ?? option.key ?? option.id ?? option.label ?? ''
        ).trim();
        const optionLabel = sanitizeDisplayText(
          option.label ?? option.title ?? option.value ?? option.key ?? ''
        );
        return { value: optionValue, label: optionLabel };
      }
      const optionValue = String(option ?? '').trim();
      return {
        value: optionValue,
        label: humanizeStoredToken(optionValue),
      };
    })
    .find((option) => option.value === raw);

  return match?.label || '';
}

function renderStructuredValue(value, field = {}) {
  if (value === null || value === undefined || value === '') return '—';

  if (typeof value === 'boolean') return value ? 'Yes' : 'No';

  if (Array.isArray(value)) {
    if (!value.length) return '—';
    return value
      .map((item) => renderStructuredValue(item, field))
      .join(', ');
  }

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  const type = normalizeStructuredFieldType(field?.type);
  const raw = String(value).trim();

  if (type === 'boolean' || type === 'checkbox' || type === 'yes no' || type === 'yes/no') {
    const lower = raw.toLowerCase();
    if (['true', '1', 'yes', 'y'].includes(lower)) return 'Yes';
    if (['false', '0', 'no', 'n'].includes(lower)) return 'No';
  }

  if (type === 'multiselect' || type === 'multi select' || type === 'multiple select') {
    let values = [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) values = parsed;
    } catch {
      values = raw.split(/[|,\n]/g);
    }

    const cleaned = values
      .map((item) => String(item ?? '').trim())
      .filter(Boolean)
      .map((item) => structuredFieldOptionLabel(field, item) || humanizeStoredToken(item));

    return cleaned.length ? cleaned.join(', ') : '—';
  }

  if (type === 'select' || type === 'dropdown' || type === 'single select') {
    return structuredFieldOptionLabel(field, raw) || humanizeStoredToken(raw);
  }

  return humanizeStoredToken(raw);
}

function participationComponentOptions(template = {}) {
  return (Array.isArray(template?.participationComponentOptions)
    ? template.participationComponentOptions
    : [])
    .map((item) => {
      const source = item && typeof item === 'object'
        ? item
        : { value: item, label: item };
      const value = sanitizeDisplayText(
        source.value || source.key || source.label || ''
      );
      return {
        value,
        label: sanitizeDisplayText(source.label || value),
      };
    })
    .filter((item) => item.value);
}

function participationComponentLabel(template = {}, value) {
  const raw = sanitizeDisplayText(value);
  if (!raw) return '';
  const match = participationComponentOptions(template)
    .find((item) => item.value === raw);
  return match?.label || humanizeStoredToken(raw);
}

function normalizeStatusToken(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/_/g, '-')
    .replace(/\s+/g, '-');
}

function isApprovedStatus(value) {
  const s = normalizeStatusToken(value);
  return s === 'approved' || s === 'observed-approved';
}

function isAwaitingDecisionStatus(value) {
  const s = normalizeStatusToken(value);
  return ['observed-submitted', 'submitted', 'pending', 'awaiting-review'].includes(s);
}

const PARTICIPATION_MODES = [
  'observed',
  'assisted',
  'performed_supervised',
  'performed_independent',
];

function normalizeParticipationMode(value) {
  const s = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  const aliases = {
    observe: 'observed', observer: 'observed', observed: 'observed',
    assist: 'assisted', assisted: 'assisted', participated: 'assisted', participant: 'assisted',
    performed: 'performed_supervised', supervised: 'performed_supervised',
    performed_supervised: 'performed_supervised', performed_under_supervision: 'performed_supervised',
    independent: 'performed_independent', performed_independent: 'performed_independent',
    independent_assigned_task: 'performed_independent',
  };
  const mapped = aliases[s] || s;
  return PARTICIPATION_MODES.includes(mapped) ? mapped : '';
}

function participationModeLabel(value) {
  const mode = normalizeParticipationMode(value);
  if (mode === 'observed') return 'Observed';
  if (mode === 'assisted') return 'Assisted / Participated';
  if (mode === 'performed_supervised') return 'Performed / Led under supervision';
  if (mode === 'performed_independent') return 'Independent assigned task';
  return '—';
}

function templateParticipationModes(template = {}) {
  return (Array.isArray(template?.participationModes) ? template.participationModes : [])
    .map((item) => ({
      ...(item || {}),
      key: normalizeParticipationMode(item?.key || item?.mode || item),
      label: sanitizeDisplayText(item?.label || participationModeLabel(item?.key || item)),
      description: sanitizeDisplayText(item?.description || ''),
    }))
    .filter((item) => item.key && item.allowed !== false);
}

function userIdString(value) {
  if (!value) return '';
  if (typeof value === 'object') return String(value._id || value.id || value.userId || '');
  return String(value);
}

// ---- Tenant-aware helpers ----
function safeJsonParse(v) {
  try {
    return JSON.parse(v);
  } catch {
    return null;
  }
}

function persistTenantSlug(slug) {
  try {
    const s = String(slug || '').trim();
    if (!s) return;
    localStorage.setItem('tenantSlug', s);
    localStorage.setItem('activeTenantSlug', s);
    sessionStorage.setItem('tenantSlug', s);
    sessionStorage.setItem('activeTenantSlug', s);
  } catch {
    // ignore storage failures
  }
}

function readTenantSlugFromStorage() {
  const candidates = [];
  try {
    const t1 = localStorage.getItem('tenantSlug');
    const t2 = localStorage.getItem('activeTenantSlug');
    const t3 = sessionStorage.getItem('tenantSlug');
    const t4 = sessionStorage.getItem('activeTenantSlug');
    if (t1) candidates.push(t1);
    if (t2) candidates.push(t2);
    if (t3) candidates.push(t3);
    if (t4) candidates.push(t4);

    const lu = safeJsonParse(localStorage.getItem('loggedInUser') || 'null');
    const su = safeJsonParse(sessionStorage.getItem('loggedInUser') || 'null');

    [lu, su].filter(Boolean).forEach((obj) => {
      const core = obj?.user || obj;
      candidates.push(
        core?.tenantSlug,
        obj?.tenantSlug,
        core?.tenant?.slug,
        obj?.tenant?.slug
      );
    });
  } catch {
    // ignore
  }

  const slug = candidates
    .map((s) => String(s || '').trim())
    .find((s) => !!s);

  return slug || '';
}

function getTenantSlug() {
  try {
    const m = (window.location.pathname || '').match(/^\/t\/([^/]+)/);
    if (m) {
      const slug = decodeURIComponent(m[1]);
      persistTenantSlug(slug);
      return slug;
    }
  } catch {
    // ignore
  }
  return readTenantSlugFromStorage();
}

function tenantUrl(path) {
  const slug = getTenantSlug();
  const p = String(path || '');
  if (!slug) return p;
  if (!p.startsWith('/')) return `/t/${slug}/${p}`.replace(/\/{2,}/g, '/');
  if (p.startsWith(`/t/${slug}`)) return p;
  return `/t/${slug}${p}`.replace(/\/{2,}/g, '/');
}

function normalizeRoleToken(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

function isInternRole(roleValue) {
  const r = normalizeRoleToken(roleValue);
  return r.includes('intern') && !r.includes('junior resident') && !r.includes('senior resident');
}

function isReviewerRole(roleValue) {
  const r = normalizeRoleToken(roleValue);
  return (
    r === 'jr' ||
    r === 'sr' ||
    r.includes('junior resident') ||
    r.includes('senior resident') ||
    r.includes('faculty') ||
    r.includes('admin') ||
    r.includes('hod') ||
    r.includes('owner')
  );
}

// Role helper: interns must never be allowed to record decisions.
// Hardened: checks multiple keys + sessionStorage so role is not missed.
function getRoleLower() {
  const candidates = [
    'loggedInUser',
    'user',
    'currentUser',
    'authUser',
    'cmfmUser',
    'portalUser',
  ];

  for (const k of candidates) {
    const u1 = safeJsonParse(localStorage.getItem(k));
    const u2 = safeJsonParse(sessionStorage.getItem(k));
    const u = u1 || u2;
    const role = u?.role || u?.user?.role || u?.profile?.role || u?.userType || u?.user?.userType;
    if (role) return String(role).toLowerCase();
  }

  const fallback =
    localStorage.getItem('role') ||
    localStorage.getItem('userRole') ||
    sessionStorage.getItem('role') ||
    sessionStorage.getItem('userRole') ||
    '';

  return String(fallback || '').toLowerCase();
}

// ---- Template / evidence helpers ----
function decorateStep(step = {}) {
  return {
    ...step,
    title: sanitizeDisplayText(step.title || step.label || `Step ${step.order || ''}`),
    description: sanitizeDisplayText(step.description || ''),
    section: sanitizeDisplayText(step.section || ''),
    label: sanitizeDisplayText(step.label || step.title || ''),
  };
}

function decorateTemplate(template = {}) {
  const steps = Array.isArray(template?.steps) ? template.steps.map(decorateStep) : [];
  return {
    ...template,
    title: sanitizeDisplayText(template?.title || template?.name || 'Guided Activity'),
    name: sanitizeDisplayText(template?.name || template?.title || 'Guided Activity'),
    category: sanitizeDisplayText(template?.category || ''),
    goals: sanitizeStringArray(template?.goals || []),
    steps,
  };
}

function decorateActivityDoc(item = {}) {
  if (!item || typeof item !== 'object') return item;
  return {
    ...item,
    title: sanitizeDisplayText(item.title || ''),
    templateTitle: sanitizeDisplayText(item.templateTitle || ''),
    templateName: sanitizeDisplayText(item.templateName || ''),
    templateCategory: sanitizeDisplayText(item.templateCategory || ''),
    corePracticeAreaTitle: sanitizeDisplayText(item.corePracticeAreaTitle || item.practiceAreaTitle || ''),
    practiceAreaTitle: sanitizeDisplayText(item.practiceAreaTitle || item.corePracticeAreaTitle || ''),
    finalGroup: sanitizeDisplayText(item.finalGroup || ''),
    competencies: sanitizeStringArray(item.competencies || []),
    skills: sanitizeStringArray(item.skills || []),
    reportingTarget: item.reportingTarget && typeof item.reportingTarget === 'object'
      ? {
          ...item.reportingTarget,
          section: sanitizeDisplayText(item.reportingTarget.section || ''),
          item: sanitizeDisplayText(item.reportingTarget.item || ''),
          notes: sanitizeDisplayText(item.reportingTarget.notes || ''),
        }
      : {},
  };
}

// Minimal template fallback so old/missing templates still render stepData.
function minimalTemplateFromDoc(item) {
  const title = sanitizeDisplayText(
    pickFirstNonEmpty(
      item?.templateTitle,
      item?.templateName,
      item?.template?.title,
      item?.template?.name,
      item?.title,
      'Guided Activity'
    )
  );

  const steps =
    Array.isArray(item?.template?.steps) ? item.template.steps :
    Array.isArray(item?.steps) ? item.steps :
    Array.isArray(item?.templateSteps) ? item.templateSteps :
    [];

  return decorateTemplate({ title, steps });
}

// If template has no steps, build steps from doc.stepData so UI always shows values.
function stepsFromStepData(doc) {
  const arr =
    (Array.isArray(doc?.stepData) && doc.stepData) ||
    (Array.isArray(doc?.stepsData) && doc.stepsData) ||
    [];

  const orders = Array.from(
    new Set(arr.map((x) => Number(x?.order)).filter((n) => Number.isFinite(n)))
  ).sort((a, b) => a - b);

  return orders.map((o) => {
    const row = arr.find((x) => Number(x?.order) === o) || {};
    return decorateStep({
      order: o,
      title: row.label || `Step ${o}`,
      description: row.section || '',
      key: row.key || '',
      label: row.label || '',
      section: row.section || '',
    });
  });
}

// ---- Structured value helpers for guided activities ----
function parseStructuredValue(raw) {
  if (raw == null) {
    return { notes: '', structured: null, showRawText: false };
  }

  if (typeof raw === 'object') {
    if (Object.prototype.hasOwnProperty.call(raw, STRUCTURED_MARKER)) {
      const body = raw[STRUCTURED_MARKER] || {};
      const { __notes, ...rest } = body || {};
      const structured =
        rest && typeof rest === 'object' && Object.keys(rest).length > 0
          ? rest
          : null;
      return {
        notes: sanitizeDisplayText(__notes || ''),
        structured,
        showRawText: false,
      };
    }
    const { __notes, ...rest } = raw;
    const structured =
      rest && typeof rest === 'object' && Object.keys(rest).length > 0
        ? rest
        : null;
    return {
      notes: sanitizeDisplayText(__notes || ''),
      structured,
      showRawText: false,
    };
  }

  if (typeof raw !== 'string') {
    return {
      notes: sanitizeDisplayText(String(raw)),
      structured: null,
      showRawText: true,
    };
  }

  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      if (Object.prototype.hasOwnProperty.call(parsed, STRUCTURED_MARKER)) {
        const body = parsed[STRUCTURED_MARKER] || {};
        const { __notes, ...rest } = body || {};
        const structured =
          rest && typeof rest === 'object' && Object.keys(rest).length > 0
            ? rest
            : null;
        return {
          notes: sanitizeDisplayText(__notes || ''),
          structured,
          showRawText: false,
        };
      }
      const { __notes, ...rest } = parsed;
      const structured =
        rest && typeof rest === 'object' && Object.keys(rest).length > 0
          ? rest
          : null;
      return {
        notes: sanitizeDisplayText(__notes || ''),
        structured,
        showRawText: false,
      };
    }
  } catch {
    // Plain-text evidence is intentionally rendered below.
  }

  return {
    notes: sanitizeDisplayText(raw),
    structured: null,
    showRawText: true,
  };
}

function prettyLabel(key) {
  if (!key) return '';
  const withSpaces = String(key)
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
  return sanitizeDisplayText(withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1));
}

function observerNameToString(value) {
  if (!value) return '';
  if (typeof value === 'string') return sanitizeDisplayText(value);
  if (typeof value === 'object') {
    return sanitizeDisplayText(
      value.name ||
      value.fullName ||
      value.displayName ||
      value.email ||
      value.username ||
      ''
    );
  }
  return sanitizeDisplayText(String(value));
}

function readLoggedInUserIdentityFromStorage() {
  const keys = [
    'loggedInUser',
    'user',
    'currentUser',
    'authUser',
    'cmfmUser',
    'portalUser',
  ];

  try {
    for (const store of [localStorage, sessionStorage]) {
      for (const key of keys) {
        const stored = safeJsonParse(store.getItem(key) || 'null');
        if (!stored || typeof stored !== 'object') continue;

        const core = stored?.user || stored;
        const userId = userIdString(
          pickFirstNonEmpty(
            core?._id,
            core?.id,
            core?.userId,
            stored?._id,
            stored?.id,
            stored?.userId
          )
        );

        const name = observerNameToString(
          core?.name ||
          core?.fullName ||
          core?.displayName ||
          stored?.name ||
          stored?.fullName ||
          stored?.displayName
        );

        const rollNo = sanitizeDisplayText(
          pickFirstNonEmpty(
            core?.rollNo,
            core?.registrationNumber,
            stored?.rollNo,
            stored?.registrationNumber
          )
        );

        const batch = sanitizeDisplayText(
          pickFirstNonEmpty(
            core?.batchName,
            core?.batch,
            stored?.batchName,
            stored?.batch
          )
        );

        const role = sanitizeDisplayText(
          pickFirstNonEmpty(core?.role, core?.userType, stored?.role, stored?.userType)
        );

        if (userId || name || rollNo || batch || role) {
          return { userId, name, rollNo, batch, role };
        }
      }
    }
  } catch {
    // Storage may be unavailable; server-supplied identity remains authoritative.
  }

  return { userId: '', name: '', rollNo: '', batch: '', role: '' };
}

function activityOwnerIdentity(item = {}, loggedInIdentity = {}) {
  const nestedUser =
    item?.internUser && typeof item.internUser === 'object'
      ? item.internUser
      : (item?.internUserId && typeof item.internUserId === 'object' ? item.internUserId : {});

  const rawUserId = pickFirstNonEmpty(
    nestedUser?._id,
    nestedUser?.id,
    typeof item?.internUserId === 'object' ? '' : item?.internUserId,
    item?.userId
  );

  const ownerUserId = userIdString(rawUserId);
  const loggedInUserId = userIdString(loggedInIdentity?.userId);

  // Strict safety rule: local/session identity may fill missing legacy owner
  // fields only when both IDs are present and are exactly the same account.
  // This prevents a reviewer from ever being substituted as another learner.
  const mayUseLoggedInFallback =
    !!ownerUserId &&
    !!loggedInUserId &&
    ownerUserId === loggedInUserId;

  const name =
    observerNameToString(
      item?.internName ||
      item?.userName ||
      item?.createdByName ||
      item?.intern?.name ||
      nestedUser
    ) ||
    observerNameToString(item?.internEmail || nestedUser?.email) ||
    (mayUseLoggedInFallback ? observerNameToString(loggedInIdentity?.name) : '');

  const rollNo = sanitizeDisplayText(
    pickFirstNonEmpty(
      item?.internRollNo,
      item?.rollNo,
      item?.registrationNumber,
      item?.enrollmentNo,
      item?.intern?.rollNo,
      item?.intern?.registrationNumber,
      nestedUser?.rollNo,
      nestedUser?.registrationNumber,
      nestedUser?.enrollmentNo,
      mayUseLoggedInFallback ? loggedInIdentity?.rollNo : ''
    )
  );

  const batch = sanitizeDisplayText(
    pickFirstNonEmpty(
      item?.internBatch,
      item?.batchName,
      item?.batchYear,
      item?.intern?.batchName,
      item?.intern?.batchYear,
      nestedUser?.batchName,
      nestedUser?.batchYear,
      mayUseLoggedInFallback ? loggedInIdentity?.batch : ''
    )
  );

  const role = sanitizeDisplayText(
    pickFirstNonEmpty(
      item?.internRole,
      item?.userRole,
      item?.intern?.role,
      nestedUser?.role,
      mayUseLoggedInFallback ? loggedInIdentity?.role : ''
    )
  );

  return {
    name,
    rollNo,
    batch,
    role,
    userId: ownerUserId,
  };
}

function getActivityPayload(res) {
  return decorateActivityDoc(res?.item || res?.activity || res?.data || res || {});
}

function getTemplatePayload(res) {
  return decorateTemplate(res?.item || res?.template || res?.data || res || {});
}

function normalizeCountValue(value) {
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

export default function ObserverSignoff() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // viewOnly is explicitly passed when an intern opens older activity from Activity Log.
  const viewOnly = !!location?.state?.viewOnly;

  const roleLower = getRoleLower();
  const isIntern = isInternRole(roleLower);
  const canReview = isReviewerRole(roleLower) && !isIntern;
  const showApprovalNavigation = canReview && !viewOnly;

  const reviewerWorkbenchPath = tenantUrl('/dashboard/approval');
  const activityLauncherPath = tenantUrl('/intern/activity/new');
  const activityLogPath = tenantUrl('/intern/activity/log');

  const backTarget =
    (location?.state?.from && String(location.state.from)) ||
    (showApprovalNavigation ? reviewerWorkbenchPath : activityLogPath);

  const [doc, setDoc] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [comments, setComments] = useState('');
  const [decision, setDecision] = useState('approved');
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionDoc, setSessionDoc] = useState(null);
  const [confirmedParticipationMode, setConfirmedParticipationMode] = useState('');
  const [participationChangeReason, setParticipationChangeReason] = useState('');
  const [reviewerIsSupervisor, setReviewerIsSupervisor] = useState(false);
  const [reviewerObserved, setReviewerObserved] = useState(false);
  const [amendOpen, setAmendOpen] = useState(false);

  // ---- Load activity + template, fallback-safe ----
  async function load() {
    try {
      setErr('');
      setOk('');
      const res = await activityApi.getActivity(id);
      const item = getActivityPayload(res);
      setDoc(item);
      const itemConfirmedMode = isApprovedStatus(item?.status)
        ? item?.confirmedParticipationMode
        : '';
      setConfirmedParticipationMode(
        normalizeParticipationMode(
          itemConfirmedMode || item?.reportedParticipationMode || item?.plannedParticipationMode || ''
        )
      );
      setParticipationChangeReason('');
      setReviewerIsSupervisor(false);
      setReviewerObserved(false);
      setAmendOpen(false);

      if (item?.activitySessionId) {
        try {
          const sessionRes = await activityApi.getSession(item.activitySessionId);
          setSessionDoc(sessionRes?.item || sessionRes?.session || sessionRes?.data || sessionRes || null);
        } catch {
          setSessionDoc(null);
        }
      } else {
        setSessionDoc(null);
      }

      // Robust template resolution with fallback.
      let tplDoc = null;
      const templateId =
        item?.templateId ||
        item?.template?._id ||
        item?.template?.id ||
        item?.templateRef ||
        item?.templateID;

      if (templateId) {
        try {
          const t = await activityApi.getTemplate(templateId);
          tplDoc = getTemplatePayload(t);
        } catch {
          tplDoc = minimalTemplateFromDoc(item);
        }
      } else {
        tplDoc = decorateTemplate(item?.template || item?.templateDoc || minimalTemplateFromDoc(item));
      }

      const steps =
        Array.isArray(tplDoc?.steps) && tplDoc.steps.length > 0
          ? tplDoc.steps.map(decorateStep)
          : stepsFromStepData(item);

      setTpl(decorateTemplate({ ...(tplDoc || {}), steps }));
    } catch (e) {
      setErr(e?.message || 'Failed to load');
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const statusRaw = String(doc?.status || '');
  const status = statusRaw.toUpperCase();
  const statusLower = normalizeStatusToken(statusRaw);

  const readOnly = viewOnly || !canReview || !isAwaitingDecisionStatus(statusLower);
  const showDecisionControls = !readOnly;
  const pageTitlePrefix = showDecisionControls ? 'Observer Sign-off' : 'Activity Evidence';

  const stepsOrdered = useMemo(
    () => (tpl?.steps || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [tpl]
  );

  if (!doc || !tpl) {
    if (err) {
      return (
        <div className="p-4">
          <Alert severity="error">{err}</Alert>
        </div>
      );
    }
    return <div className="p-4">Loading…</div>;
  }

  const observerName = observerNameToString(
    doc?.observerName || doc?.observer || doc?.reviewer || doc?.reviewerName
  );
  const approverName =
    observerNameToString(doc?.approverName) ||
    observerNameToString(doc?.approvedBy) ||
    observerNameToString(doc?.signoffBy);
  const loggedInIdentity = readLoggedInUserIdentityFromStorage();
  const ownerIdentity = activityOwnerIdentity(doc, loggedInIdentity);

  const title = sanitizeDisplayText(
    pickFirstNonEmpty(
      tpl?.title,
      doc?.title,
      doc?.templateTitle,
      doc?.templateName,
      'Observer Sign-off'
    )
  );

  const centre = sanitizeDisplayText(doc?.centre || doc?.chc || doc?.location || '—');
  const dt = doc?.date || doc?.createdAt || doc?.updatedAt || '';
  const when = dt ? new Date(dt).toDateString() : '—';
  const target = doc?.reportingTarget || {};
  const corePracticeAreaText = corePracticeAreaDisplayText(
    doc?.corePracticeAreaTitle ||
    doc?.practiceAreaTitle ||
    doc?.finalGroup ||
    target?.section
  );
  const counts = doc?.counts || {};
  const roleAwareModes = templateParticipationModes(tpl);
  const roleAware = roleAwareModes.length > 0;
  const plannedMode = normalizeParticipationMode(doc?.plannedParticipationMode);
  const reportedMode = normalizeParticipationMode(doc?.reportedParticipationMode);
  const approvedMode = isApprovedStatus(statusRaw)
    ? normalizeParticipationMode(doc?.confirmedParticipationMode)
    : '';
  const participationComponents = sanitizeStringArray(doc?.participationComponents || []);
  const checklistContribution =
    isApprovedStatus(statusRaw) &&
    doc?.checklistContribution &&
    typeof doc.checklistContribution === 'object' &&
    doc.checklistContribution.applied === true
      ? doc.checklistContribution
      : null;
  const roleAwareCountCards = checklistContribution
    ? [
        ['Consultations', counts.consultations],
        ['Procedures', counts.procedures],
        ['Immunizations', counts.immunizations],
        ['Observed', checklistContribution.observed],
        ['Assisted', checklistContribution.assisted],
        ['Performed under supervision', checklistContribution.performedSupervised],
        ['Independent assigned task', checklistContribution.performedIndependent],
        ['Performed total', checklistContribution.performed],
      ]
    : null;
  const canAmendApproved = canReview && !viewOnly && isApprovedStatus(statusRaw) && roleAware;

  function getStepData(order) {
    const arr =
      (Array.isArray(doc.stepData) && doc.stepData) ||
      (Array.isArray(doc.stepsData) && doc.stepsData) ||
      [];
    const o = Number(order);
    return arr.find((x) => x && Number(x.order) === o) || {};
  }

  async function amendParticipation() {
    try {
      setErr('');
      setOk('');
      const mode = normalizeParticipationMode(confirmedParticipationMode);
      const reason = participationChangeReason.trim();
      if (!mode) {
        setErr('Select the corrected participation level.');
        return;
      }
      if (!reason) {
        setErr('An amendment reason is required.');
        return;
      }
      setBusy(true);
      await activityApi.amendParticipation(id, {
        confirmedParticipationMode: mode,
        reason,
        comments: comments?.trim() || '',
        reviewerIsSupervisor,
        reviewerObserved,
      });
      setOk('Participation record amended ✓');
      await load();
    } catch (e) {
      setErr(e?.message || 'Failed to amend participation');
    } finally {
      setBusy(false);
    }
  }

  async function act() {
    try {
      setErr('');
      setOk('');
      setBusy(true);

      // Guard: never allow interns, view-only screens, unsupported roles, or non-awaiting statuses to sign off.
      if (readOnly) {
        setErr('You are not allowed to record observer decisions from this view.');
        return;
      }

      const reportedMode = normalizeParticipationMode(doc?.reportedParticipationMode);
      const confirmedMode = normalizeParticipationMode(confirmedParticipationMode || reportedMode);
      if (decision === 'approved' && templateParticipationModes(tpl).length > 0) {
        if (!confirmedMode) {
          setErr('Confirm the intern participation level before approving.');
          return;
        }
        if (reportedMode && confirmedMode !== reportedMode && !participationChangeReason.trim()) {
          setErr('Give a reason when changing the intern-reported participation level.');
          return;
        }
      }

      const signoffPayload = {
        decision, // 'approved' | 'returned' | 'repeat'
        comments: comments?.trim() || '',
        repeat: decision === 'repeat',
      };

      // Only an approval finalises reviewer-confirmed participation. Return and
      // repeat actions deliberately carry no confirmation/supervision claim.
      if (decision === 'approved') {
        signoffPayload.confirmedParticipationMode = confirmedMode;
        signoffPayload.participationChangeReason = participationChangeReason?.trim() || '';
        signoffPayload.reviewerIsSupervisor = reviewerIsSupervisor;
        signoffPayload.reviewerObserved = reviewerObserved;
      }

      await activityApi.observerSignoff(id, signoffPayload);
      setOk('Recorded ✓');

      // Return exactly where we came from when possible. This keeps tenant prefix and avoids login redirect loops.
      navigate(backTarget);
    } catch (e) {
      setErr(e?.message || 'Failed to record decision');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-xl font-semibold">
            {pageTitlePrefix}: {title}
          </h1>
          <Typography variant="body2" sx={{ opacity: 0.78 }}>
            Review submitted guided-activity evidence here. Guided activities remain separate from citizen OPD workflow; approved evidence can update the rotation report through explicit backend sync.
          </Typography>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <Link
            to={activityLauncherPath}
            className="underline opacity-80 hover:opacity-100"
          >
            ← Start Activity
          </Link>
          <Link
            to={activityLogPath}
            className="underline opacity-80 hover:opacity-100"
          >
            Activity Log →
          </Link>
          {showApprovalNavigation && (
            <Link
              to={backTarget}
              className="underline opacity-80 hover:opacity-100"
            >
              ← Back to Approvals
            </Link>
          )}
        </div>
      </div>

      {err && <Alert severity="error">{err}</Alert>}
      {ok && <Alert severity="success">{ok}</Alert>}

      {(isIntern || viewOnly || !canReview) && (
        <Alert severity="info">
          Activity evidence view: observer sign-off controls are hidden for intern users and other non-reviewer roles.
        </Alert>
      )}

      {canReview && !viewOnly && !showDecisionControls && (
        <Alert severity={isApprovedStatus(statusRaw) ? 'success' : 'info'}>
          {isApprovedStatus(statusRaw)
            ? 'This run is already approved. Confirmed participation is retained with an audit trail and can be amended by an authorised reviewer when necessary.'
            : 'This run is not currently awaiting observer decision.'}
        </Alert>
      )}

      <Box className="text-sm opacity-80">
        {when} • {centre}
        {doc?.shift ? ` • ${sanitizeDisplayText(doc.shift)}` : ''} • Status:{' '}
        <b>{status || '—'}</b>
      </Box>

      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
          Activity record owner
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 0.5, sm: 2 }}
          sx={{ flexWrap: 'wrap', alignItems: { sm: 'center' } }}
        >
          <Typography variant="body2">
            <strong>Intern / learner:</strong> {ownerIdentity.name || '—'}
          </Typography>
          <Typography variant="body2">
            <strong>Roll / registration:</strong> {ownerIdentity.rollNo || '—'}
          </Typography>
          {ownerIdentity.batch && (
            <Typography variant="body2">
              <strong>Batch:</strong> {ownerIdentity.batch}
            </Typography>
          )}
          {ownerIdentity.role && (
            <Typography variant="body2">
              <strong>Role:</strong> {ownerIdentity.role}
            </Typography>
          )}
          {ownerIdentity.userId && (
            <Typography variant="caption" sx={{ opacity: 0.72 }}>
              Account ID: {ownerIdentity.userId}
            </Typography>
          )}
        </Stack>
      </Box>

      <Box className="flex flex-wrap items-center gap-2">
        {doc?.sourceFlow && <Chip size="small" variant="outlined" label={`Flow: ${sanitizeDisplayText(doc.sourceFlow)}`} />}
        {doc?.activityKey && <Chip size="small" variant="outlined" label={`Key: ${sanitizeDisplayText(doc.activityKey)}`} />}
        {corePracticeAreaText && (
          <Chip
            size="small"
            variant="outlined"
            label={`${CORE_PRACTICE_AREA_LABEL}: ${corePracticeAreaText}`}
          />
        )}
        {target?.item && (
          <Chip size="small" variant="outlined" label={`Rotation item: ${sanitizeDisplayText(target.item)}`} />
        )}
        {observerName && <Chip size="small" label={`Observer: ${observerName}`} />}
        {approverName && <Chip size="small" color="success" label={`Approver: ${approverName}`} />}
      </Box>

      {(sanitizeStringArray(doc?.competencies || []).length > 0 || sanitizeStringArray(doc?.skills || []).length > 0) && (
        <Box className="space-y-1">
          {sanitizeStringArray(doc?.competencies || []).length > 0 && (
            <Typography variant="body2" sx={{ opacity: 0.82 }}>
              <strong>Competencies:</strong> {sanitizeStringArray(doc.competencies).join('; ')}
            </Typography>
          )}
          {sanitizeStringArray(doc?.skills || []).length > 0 && (
            <Typography variant="body2" sx={{ opacity: 0.82 }}>
              <strong>Skills:</strong> {sanitizeStringArray(doc.skills).join('; ')}
            </Typography>
          )}
        </Box>
      )}

      {sessionDoc && Array.isArray(sessionDoc?.participants) && sessionDoc.participants.length > 0 && (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
          <Typography variant="subtitle1" fontWeight={700}>Activity team</Typography>
          <Typography variant="body2" sx={{ opacity: 0.75, mb: 1 }}>
            One real-world activity session; each intern keeps an individual evidence record.
          </Typography>
          <Stack spacing={0.75}>
            {sessionDoc.participants.map((participant, index) => {
              const isCurrentIntern = userIdString(participant?.userId) === userIdString(doc?.internUserId);
              return (
                <Box key={participant?._id || participant?.userId || index} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight={isCurrentIntern ? 700 : 500}>
                    {sanitizeDisplayText(participant?.name || 'Participant')}
                  </Typography>
                  {participant?.role && <Chip size="small" variant="outlined" label={sanitizeDisplayText(participant.role)} />}
                  {(participant?.functions || []).map((fn) => (
                    <Chip key={fn} size="small" label={sanitizeDisplayText(fn)} />
                  ))}
                  {participant?.plannedParticipationMode && (
                    <Chip size="small" color={isCurrentIntern ? 'primary' : 'default'} label={`Planned: ${participationModeLabel(participant.plannedParticipationMode)}`} />
                  )}
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}

      {roleAware && (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
          <Typography variant="subtitle1" fontWeight={700}>Participation confirmation</Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
            <Chip label={`Planned: ${participationModeLabel(plannedMode)}`} variant="outlined" />
            <Chip label={`Intern reported: ${participationModeLabel(reportedMode)}`} color="primary" variant="outlined" />
            {approvedMode && <Chip label={`Confirmed: ${participationModeLabel(approvedMode)}`} color="success" />}
          </Stack>
          {doc?.participationNote && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Intern note:</strong> {sanitizeDisplayText(doc.participationNote)}
            </Typography>
          )}

          {participationComponents.length > 0 && (
            <Box sx={{ mt: 1.25 }}>
              <Typography variant="body2" sx={{ mb: 0.75 }}>
                <strong>Scope / assigned component(s):</strong>
              </Typography>
              <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                {participationComponents.map((component) => (
                  <Chip
                    key={component}
                    size="small"
                    variant="outlined"
                    label={participationComponentLabel(tpl, component)}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {(showDecisionControls || (canAmendApproved && amendOpen)) && (
            <Box sx={{ mt: 1.5 }}>
              <TextField
                select
                fullWidth
                size="small"
                label={canAmendApproved && amendOpen ? 'Corrected participation level' : 'Reviewer-confirmed participation'}
                value={confirmedParticipationMode}
                onChange={(e) => setConfirmedParticipationMode(e.target.value)}
              >
                {roleAwareModes.map((mode) => (
                  <MenuItem key={mode.key} value={mode.key}>
                    {mode.label || participationModeLabel(mode.key)}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                fullWidth
                multiline
                minRows={2}
                sx={{ mt: 1.5 }}
                label={(reportedMode && normalizeParticipationMode(confirmedParticipationMode) !== reportedMode) || (canAmendApproved && amendOpen)
                  ? 'Reason for participation change (required)'
                  : 'Participation confirmation note (optional)'}
                value={participationChangeReason}
                onChange={(e) => setParticipationChangeReason(e.target.value)}
                placeholder="Example: Intern assisted with delivery and newborn care; principal delivery was conducted by JR."
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ mt: 1 }}>
                <FormControlLabel
                  control={<Checkbox checked={reviewerObserved} onChange={(e) => setReviewerObserved(e.target.checked)} />}
                  label="I directly observed this intern's participation"
                />
                <FormControlLabel
                  control={<Checkbox checked={reviewerIsSupervisor} onChange={(e) => setReviewerIsSupervisor(e.target.checked)} />}
                  label="I was the supervisor for this intern"
                />
              </Stack>
            </Box>
          )}

          {canAmendApproved && !amendOpen && (
            <Button size="small" variant="outlined" sx={{ mt: 1.5 }} onClick={() => {
              setConfirmedParticipationMode(approvedMode || reportedMode || plannedMode || '');
              setParticipationChangeReason('');
              setAmendOpen(true);
            }}>
              Amend Participation / Supervision Record
            </Button>
          )}

          {canAmendApproved && amendOpen && (
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
              <Button variant="contained" onClick={amendParticipation} disabled={busy}>
                {busy ? 'Amending…' : 'Save Amendment'}
              </Button>
              <Button variant="text" onClick={() => {
                setAmendOpen(false);
                setParticipationChangeReason('');
                setConfirmedParticipationMode(approvedMode || reportedMode || plannedMode || '');
              }} disabled={busy}>
                Cancel
              </Button>
            </Stack>
          )}
        </Box>
      )}

      {roleAwareCountCards && (
        <Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            Activity totals / confirmed checklist contribution
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', opacity: 0.72, mb: 1 }}>
            Participation totals below reflect the confirmed contribution from this approved guided activity.
          </Typography>
          <Box className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {roleAwareCountCards.map(([label, value]) => (
              <Box key={label} className="p-2 border rounded-md dark:border-neutral-700">
                <Typography variant="caption" sx={{ opacity: 0.72 }}>{label}</Typography>
                <Typography variant="subtitle1" fontWeight={700}>{normalizeCountValue(value)}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {!roleAwareCountCards && counts && Object.keys(counts).length > 0 && (
        <Box className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {[
            ['Consultations', counts.consultations],
            ['Procedures', counts.procedures],
            ['Immunizations', counts.immunizations],
            ['Observed', counts.observed],
            ['Performed', counts.performed],
          ].map(([label, value]) => (
            <Box key={label} className="p-2 border rounded-md dark:border-neutral-700">
              <Typography variant="caption" sx={{ opacity: 0.72 }}>{label}</Typography>
              <Typography variant="subtitle1" fontWeight={700}>{normalizeCountValue(value)}</Typography>
            </Box>
          ))}
        </Box>
      )}

      <Divider />

      {/* Step-by-step evidence snapshot */}
      <div className="space-y-2 text-sm">
        {stepsOrdered.length === 0 && (
          <Alert severity="info">
            No step template found for this activity. If step data exists, it will appear here once template steps are available.
          </Alert>
        )}

        {stepsOrdered.map((s) => {
          const a = getStepData(s.order);
          const { notes, structured, showRawText } = parseStructuredValue(a.valueText);
          const structuredFieldMap = new Map(
            structuredFieldDefinitions(s).map((field) => [field.key, field])
          );
          const hasCount =
            typeof a.valueCount === 'number' &&
            !Number.isNaN(a.valueCount);
          const hasBool = typeof a.valueBool === 'boolean';
          const photos =
            Array.isArray(a.photos) && a.photos.length > 0
              ? a.photos
              : [];

          return (
            <div
              key={s.order}
              className="p-3 border rounded-md dark:border-neutral-700"
            >
              <div className="font-medium">
                {s.order}. {sanitizeDisplayText(a.label || s.title || `Step ${s.order}`)}
              </div>
              {(a.section || s.section) && (
                <div className="opacity-70">{sanitizeDisplayText(a.section || s.section)}</div>
              )}
              {s.description && (
                <div className="opacity-80">{sanitizeDisplayText(s.description)}</div>
              )}

              {structured && (
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                  {Object.entries(structured).map(([k, v]) => {
                    const field = structuredFieldMap.get(k) || {};
                    return (
                      <div key={k}>
                        <span className="font-semibold">
                          {field.label || prettyLabel(k)}:
                        </span>{' '}
                        <span>{renderStructuredValue(v, field)}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {notes && (
                <div className="mt-2">
                  <span className="font-semibold">Notes:</span>{' '}
                  <span>{sanitizeDisplayText(notes)}</span>
                </div>
              )}

              {showRawText && !structured && !notes && a.valueText && (
                <div className="mt-2">
                  <span className="font-semibold">Notes:</span>{' '}
                  <span>{sanitizeDisplayText(a.valueText)}</span>
                </div>
              )}

              {hasCount && (
                <div className="mt-2">
                  <span className="font-semibold">Count:</span>{' '}
                  <span>{normalizeCountValue(a.valueCount)}</span>
                </div>
              )}
              {hasBool && (
                <div className="mt-2">
                  <span className="font-semibold">Completed:</span>{' '}
                  <span>{a.valueBool ? 'Yes' : 'No'}</span>
                </div>
              )}
              {photos.length > 0 && (
                <div className="mt-2">
                  <span className="font-semibold">Photos:</span>{' '}
                  <span>{photos.map((x) => sanitizeDisplayText(x)).join(', ')}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Observer decision section: visible only to reviewer-side roles in decidable states */}
      {showDecisionControls && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
            <TextField
              label="Observer decision"
              select
              value={decision}
              onChange={(e) => setDecision(e.target.value)}
            >
              <MenuItem value="approved">Approve</MenuItem>
              <MenuItem value="returned">Return (needs fixes)</MenuItem>
              <MenuItem value="repeat">Repeat Required</MenuItem>
            </TextField>

            <TextField
              className="md:col-span-2"
              label="Observer comments"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
          </div>

          <div className="flex gap-2 mt-3">
            <Button
              variant="contained"
              onClick={act}
              disabled={busy}
            >
              {busy ? 'Recording…' : 'Record Decision'}
            </Button>
            <Button
              variant="text"
              onClick={() => navigate(backTarget)}
              disabled={busy}
            >
              Back to Approvals
            </Button>
          </div>
        </>
      )}

      {!showDecisionControls && (
        <div className="flex gap-2 mt-3">
          <Button
            variant="text"
            onClick={() => navigate(backTarget)}
          >
            {showApprovalNavigation ? 'Back to Approvals' : 'Back to Activity Log'}
          </Button>
        </div>
      )}
    </div>
  );
}
